import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { MdModeEditOutline } from 'react-icons/md';
import { IoMdClose } from 'react-icons/io';
import {
  FaCheckDouble,
  FaCrown,
  FaEllipsisV,
  FaRegBookmark,
  FaRegCopy,
  FaRegHeart,
} from 'react-icons/fa';
import { BsFillTrashFill } from 'react-icons/bs';

import Link from 'next/link';

import { useTheme } from 'styled-components';
import { useSavedPrompts } from '~/modules/saved/providers/saved-prompts';

import { useToast } from '~/common/providers/toast';
import { useCurrentTheme } from '~/common/providers/theme';
import { useAuth } from '~/common/providers/auth';
import { UserTypesEnum } from '~/common/enums/user-types.enum';
import { UserSubscriptionTypesEnum } from '~/common/enums/user-subscription-types.enum';
import { Typography } from '~/common/components/Typograph';
import { api } from '~/common/api';

import {
  AdminFloatingMenuWrapper,
  AdminMenuDropdown,
  AdminMenuItem,
  AdminMenuTrigger,
  ActionButtonsColumn,
  Bottom,
  BottomRow,
  Content,
  CopyButton,
  Hoverable,
  LikesButton,
  MediaOverlay,
  MediaWrapper,
  PlanTierBadge,
  PromptItemRoot,
  TitleChip,
  TopRow,
} from './styles';
import { UnauthorizedModal } from '../UnauthorizedModal';
import { PromptMediaCarousel } from '../PromptMediaCarousel';
import { IPromptListItem } from '../../interfaces/i-prompt-list-item';
import { IPromptDetails } from '../../interfaces/i-prompt-details';
import { PromptTypesEnum } from '../../enums/prompt-types-enum';

interface IPromptItemProps {
  prompt: IPromptListItem;
  onClick?: () => void;
  togglePromptApproval?: (prompt: IPromptListItem) => Promise<void>;
  handleOpenForbiddenAccessModal?: (type: UserSubscriptionTypesEnum) => void;
  onUnsaveConfirm?: (promptId: string) => void;
  onEditPrompt?: (promptId: string) => void;
  onDeletePrompt?: (promptId: string) => void;
  tierOnly?: boolean;
  forceSaved?: boolean;
}

const PromptItemComponent: FC<IPromptItemProps> = ({
  prompt,
  tierOnly = false,
  forceSaved = false,
  togglePromptApproval,
  onClick,
  handleOpenForbiddenAccessModal,
  onUnsaveConfirm,
  onEditPrompt,
  onDeletePrompt,
}) => {
  const {
    user: { type: userType, subscription, subscriptionStatus } = {},
    loggedIn,
    savedPrompts,
    hideDescriptions,
  } = useAuth();
  const theme = useTheme();
  const itemRef = useRef<HTMLLIElement>(null);
  const adminMenuRef = useRef<HTMLDivElement>(null);
  const { savePrompt, unsavePrompt } = useSavedPrompts();
  const { showToast } = useToast();
  const { type: currentTheme } = useCurrentTheme();

  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [redirectLoginUrl, setRedirectLoginUrl] = useState('');
  const [isUnauthorizedModalOpen, setIsUnauthorizedModalOpen] =
    useState<string>('');
  const [optimisticLiked, setOptimisticLiked] = useState(
    Boolean(prompt.liked)
  );
  const [optimisticLikes, setOptimisticLikes] = useState(prompt.likes || 0);

  const {
    id,
    likes,
    compactMediaUrl,
    mediaUrl,
    title,
    liked,
    type,
    planTier,
    approved,
  } = useMemo(
    () => ({
      ...prompt,
      liked: optimisticLiked,
      likes: optimisticLikes,
    }),
    [optimisticLiked, optimisticLikes, prompt]
  );

  useEffect(() => {
    const element = itemRef.current;
    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.1,
        rootMargin: '120px 0px',
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setOptimisticLiked(Boolean(prompt.liked));
    setOptimisticLikes(prompt.likes || 0);
  }, [prompt.id, prompt.liked, prompt.likes]);

  useEffect(() => {
    const closeMenuOnOutsideClick = (event: MouseEvent): void => {
      if (
        adminMenuRef.current &&
        !adminMenuRef.current.contains(event.target as Node)
      ) {
        setAdminMenuOpen(false);
      }
    };

    if (adminMenuOpen) {
      document.addEventListener('mousedown', closeMenuOnOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', closeMenuOnOutsideClick);
    };
  }, [adminMenuOpen]);

  const handleLikePrompt = useCallback(async () => {
    const method = liked ? 'delete' : 'post';

    try {
      await api[method](`/prompts/${id}/like`);
      setOptimisticLiked((prevState) => !prevState);
      setOptimisticLikes((prevState) =>
        liked ? Math.max(0, (prevState || 1) - 1) : (prevState || 0) + 1
      );
    } catch {
      showToast({
        severity: 'error',
        title: 'Erro',
        description: `Houve um erro ao ${
          liked ? 'descurtir' : 'curtir'
        } o prompt.`,
      });
    }
  }, [id, liked, showToast]);

  const handleCopy = useCallback(async () => {
    try {
      const { data: content } = await api.get<IPromptDetails>(`/prompts/${id}`);
      api.post(`/prompts/${id}/register-copy`);
      navigator?.clipboard?.writeText(content.prompt);
      showToast({
        severity: 'success',
        title: 'Sucesso',
        description: 'Prompt copiada para a area de transferencia.',
      });
    } catch {
      showToast({
        severity: 'error',
        title: 'Erro',
        description: 'Houve um erro ao copiar a descricao do prompt.',
      });
    }
  }, [id, showToast]);

  const isSaved = useMemo(
    () => forceSaved || savedPrompts.includes(id),
    [forceSaved, id, savedPrompts]
  );

  const handleSaveToggle = useCallback(async () => {
    if (isSaved) {
      if (onUnsaveConfirm) {
        onUnsaveConfirm(id);
        return;
      }

      await unsavePrompt(id);
      return;
    }

    await savePrompt(id);
  }, [id, isSaved, onUnsaveConfirm, savePrompt, unsavePrompt]);

  const refundedAccessRevoked = useMemo(
    () => subscriptionStatus === 'refunded',
    [subscriptionStatus]
  );

  const hasPromptAccess = useMemo(() => {
    if (userType === UserTypesEnum.ADMIN) {
      return true;
    }

    if (refundedAccessRevoked) {
      return !planTier || planTier === UserSubscriptionTypesEnum.FREE;
    }

    return (
      subscription === UserSubscriptionTypesEnum.PREMIUM ||
      !planTier ||
      planTier === UserSubscriptionTypesEnum.FREE ||
      (planTier === UserSubscriptionTypesEnum.BASIC &&
        subscription === UserSubscriptionTypesEnum.BASIC)
    );
  }, [planTier, refundedAccessRevoked, subscription, userType]);

  const shouldRenderPromptTitle = useMemo(() => {
    if (type === PromptTypesEnum.AGENT) {
      return true;
    }

    return !tierOnly && hasPromptAccess && !hideDescriptions;
  }, [hasPromptAccess, hideDescriptions, tierOnly, type]);

  const shouldAnimateMedia = isVisible;
  const shouldPlayVideos = isVisible && isHovered;

  return (
    <>
      {!!isUnauthorizedModalOpen && (
        <UnauthorizedModal
          open={isUnauthorizedModalOpen}
          afterLoginRedirectUrl={redirectLoginUrl}
          close={() => {
            setIsUnauthorizedModalOpen('');
            setRedirectLoginUrl('');
          }}
        />
      )}

      <PromptItemRoot
        ref={itemRef}
        $visible={isVisible}
        onClick={onClick || undefined}
      >
        {userType === UserTypesEnum.ADMIN &&
          (onEditPrompt || onDeletePrompt) && (
            <AdminFloatingMenuWrapper ref={adminMenuRef} $open={adminMenuOpen}>
              <AdminMenuTrigger
                $open={adminMenuOpen}
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  setAdminMenuOpen((prevState) => !prevState);
                }}
              >
                <FaEllipsisV size={12} color={theme.neutral0} />
              </AdminMenuTrigger>

              <AdminMenuDropdown $open={adminMenuOpen}>
                {onEditPrompt && (
                  <AdminMenuItem
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      setAdminMenuOpen(false);
                      onEditPrompt(id);
                    }}
                  >
                    <MdModeEditOutline size={14} color={theme.neutral0} />
                    <Typography.Label size="x-small" color="neutral0">
                      Editar
                    </Typography.Label>
                  </AdminMenuItem>
                )}

                {onDeletePrompt && (
                  <AdminMenuItem
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      setAdminMenuOpen(false);
                      onDeletePrompt(id);
                    }}
                  >
                    <BsFillTrashFill size={12} color={theme.red} />
                    <Typography.Label size="x-small" color="neutral0">
                      Apagar
                    </Typography.Label>
                  </AdminMenuItem>
                )}
              </AdminMenuDropdown>
            </AdminFloatingMenuWrapper>
          )}

        <Link href={`/prompts/${id}`} passHref>
          <Hoverable
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <MediaWrapper>
              <PromptMediaCarousel
                mediaUrls={mediaUrl}
                compactMediaUrls={compactMediaUrl}
                type={type}
                alt={title}
                fallbackToDefault
                hoverScale={1.08}
                isActive={shouldAnimateMedia}
                playVideos={shouldPlayVideos}
              />
            </MediaWrapper>
            <MediaOverlay />

            <Content>
              <TopRow>
                {shouldRenderPromptTitle && (
                  <TitleChip>
                    <Typography.Label
                      size="x-small"
                      color={
                        currentTheme === 'light' ? 'neutral1000' : 'neutral0'
                      }
                    >
                      {title.length > 19 ? `${title.substring(0, 19)}...` : title}
                    </Typography.Label>
                  </TitleChip>
                )}

                {!tierOnly && (
                  <ActionButtonsColumn>
                    <LikesButton
                      onClick={(event) => {
                        event.stopPropagation();
                        event.preventDefault();

                        if (!loggedIn) {
                          setRedirectLoginUrl(`/prompts/${id}`);
                          setIsUnauthorizedModalOpen(
                            'Voce precisa estar logado para salvar este prompt.'
                          );
                          return;
                        }

                        if (!hasPromptAccess) {
                          handleOpenForbiddenAccessModal?.(planTier);
                          return;
                        }

                        handleSaveToggle();
                      }}
                    >
                      <FaRegBookmark
                        size={14}
                        color={isSaved ? theme.primary : theme.neutral0}
                      />
                    </LikesButton>

                    <LikesButton
                      onClick={(event) => {
                        event.stopPropagation();
                        event.preventDefault();

                        if (!loggedIn) {
                          setRedirectLoginUrl(`/prompts/${id}`);
                          setIsUnauthorizedModalOpen(
                            'Voce precisa estar logado para curtir este prompt.'
                          );
                          return;
                        }

                        if (!hasPromptAccess) {
                          handleOpenForbiddenAccessModal?.(planTier);
                          return;
                        }

                        handleLikePrompt();
                      }}
                    >
                      <FaRegHeart
                        size={14}
                        color={liked ? theme.red : theme.neutral0}
                      />
                      <Typography.Label
                        size="x-small"
                        color={
                          currentTheme === 'light' ? 'neutral1000' : 'neutral0'
                        }
                      >
                        {likes}
                      </Typography.Label>
                    </LikesButton>
                  </ActionButtonsColumn>
                )}
              </TopRow>

              <Bottom>
                <BottomRow>
                  {!tierOnly && (
                    <CopyButton
                      onClick={(event) => {
                        event.stopPropagation();
                        event.preventDefault();

                        if (!loggedIn) {
                          setRedirectLoginUrl(`/prompts/${id}`);
                          setIsUnauthorizedModalOpen(
                            'Voce precisa estar logado para copiar ou baixar este prompt.'
                          );
                          return;
                        }

                        if (!hasPromptAccess) {
                          handleOpenForbiddenAccessModal?.(planTier);
                          return;
                        }

                        handleCopy();
                      }}
                    >
                      <FaRegCopy size={14} />
                    </CopyButton>
                  )}

                  {!tierOnly && togglePromptApproval && userType === UserTypesEnum.ADMIN && (
                    <CopyButton
                      onClick={async (event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        await togglePromptApproval(prompt);
                      }}
                    >
                      {approved ? (
                        <IoMdClose size={14} color={theme.red} />
                      ) : (
                        <FaCheckDouble size={14} color={theme.primary} />
                      )}
                    </CopyButton>
                  )}

                  <PlanTierBadge>
                    {planTier === UserSubscriptionTypesEnum.PREMIUM && (
                      <FaCrown color={theme.yellow} size={12} />
                    )}
                    <Typography.Label
                      size="x-small"
                      color={
                        currentTheme === 'light' ? 'neutral1000' : 'neutral0'
                      }
                    >
                      {planTier === UserSubscriptionTypesEnum.FREE
                        ? 'Gratis'
                        : 'Premium'}
                    </Typography.Label>
                  </PlanTierBadge>
                </BottomRow>
              </Bottom>
            </Content>
          </Hoverable>
        </Link>
      </PromptItemRoot>
    </>
  );
};

export const PromptItem = memo(
  PromptItemComponent,
  (previousProps, nextProps) =>
    previousProps.prompt === nextProps.prompt &&
    previousProps.onClick === nextProps.onClick &&
    previousProps.togglePromptApproval === nextProps.togglePromptApproval &&
    previousProps.handleOpenForbiddenAccessModal ===
      nextProps.handleOpenForbiddenAccessModal &&
    previousProps.onUnsaveConfirm === nextProps.onUnsaveConfirm &&
    previousProps.onEditPrompt === nextProps.onEditPrompt &&
    previousProps.onDeletePrompt === nextProps.onDeletePrompt &&
    previousProps.tierOnly === nextProps.tierOnly &&
    previousProps.forceSaved === nextProps.forceSaved
);
